/**
 * Pre-renders glow sprites as OffscreenCanvas images.
 * Each sprite is cached by (color, radius) key. Used instead of
 * ctx.shadowBlur/shadowColor for O(1) per-frame glow rendering.
 *
 * BL-285 — the cache key QUANTISES the radius. Every live caller feeds a
 * *continuous* radius (the enemy halo is `radius × breathe(…) × 1.6`, the tower
 * bloom breathes too), so the raw float key missed on virtually every frame:
 * each glowing entity allocated a fresh ~(4r)² canvas + radial-gradient fill
 * per frame and evicted the LRU, i.e. the exact opposite of the "O(1) per-frame
 * glow" this class exists for — the single most expensive thing on the board on
 * a fill-rate-bound phone. Quantising costs nothing visually because
 * {@link GlowSpriteCache.draw} scales the sprite back to the EXACT requested
 * radius: the bucket only sets the sprite's internal resolution, and a soft
 * radial blur upscaled by ≤ a bucket width is imperceptible.
 */
export class GlowSpriteCache {
  private cache = new Map<string, HTMLCanvasElement>();
  private static readonly MAX_ENTRIES = 128;
  /**
   * Latched once a 2D context has proved unobtainable. Without it the null path
   * still `createElement`s a canvas on EVERY call before discovering there is no
   * context — roughly 280 orphaned elements a frame at a late wave, on precisely
   * the device that produces the condition (WebKit past its live-context cap).
   * A host that cannot give one context will not start giving them later, so the
   * honest answer is to ask once.
   */
  private contextUnavailable = false;

  private key(color: string, radius: number): string {
    return `${color}:${radius}`;
  }

  /**
   * Snap a requested radius onto the sprite ladder. Steps widen with size (the
   * relative error — what the eye reads on a blur — stays roughly constant),
   * which collapses the whole live radius spectrum onto ~20 buckets.
   */
  private quantize(radius: number): number {
    if (!(radius > 0)) return 1;
    if (radius < 8) return Math.max(1, Math.round(radius));
    if (radius < 20) return Math.round(radius / 2) * 2;
    return Math.round(radius / 4) * 4;
  }

  /**
   * Get (or create) a glow sprite for given color and radius.
   * The returned canvas is `radius * 4` wide/tall (enough for soft falloff),
   * built at the {@link quantize}d radius — callers that need an exact size
   * should go through {@link draw}, which rescales on the way out.
   * Returns null in headless environments (tests).
   */
  get(color: string, radius: number): HTMLCanvasElement | null {
    if (typeof document === "undefined" || this.contextUnavailable) return null;

    radius = this.quantize(radius);
    const k = this.key(color, radius);
    let sprite = this.cache.get(k);
    if (sprite) {
      // LRU: move to end
      this.cache.delete(k);
      this.cache.set(k, sprite);
      return sprite;
    }

    const size = Math.ceil(radius * 4);
    sprite = document.createElement("canvas");
    sprite.width = size;
    sprite.height = size;
    // A 2D context is NOT guaranteed just because `document` exists: WebKit
    // caps live canvas contexts and hands back null past the cap, and jsdom
    // returns null unless the `canvas` package is installed. The old `!`
    // assertion turned either case into a hard crash inside the render loop —
    // glow is decoration, so it degrades to "no glow" instead.
    const ctx = sprite.getContext("2d");
    if (!ctx) {
      this.contextUnavailable = true;
      return null;
    }
    const cx = size / 2;
    const cy = size / 2;
    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius * 1.8);
    grad.addColorStop(0, color);
    grad.addColorStop(0.4, color + "88");
    grad.addColorStop(1, color + "00");
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cy, radius * 1.8, 0, Math.PI * 2);
    ctx.fill();

    this.cache.set(k, sprite);

    // CR-11 (A5): Evict oldest entry if cache exceeds cap
    if (this.cache.size > GlowSpriteCache.MAX_ENTRIES) {
      const oldest = this.cache.keys().next().value;
      if (oldest !== undefined) this.cache.delete(oldest);
    }

    return sprite;
  }

  /**
   * Draw a cached glow sprite centered at (x, y), rescaled from its quantised
   * bucket to the EXACT radius asked for — so a breathing halo still grows and
   * shrinks continuously (no stair-stepping) off one cached sprite.
   * No-op if environment doesn't support canvas creation.
   */
  draw(
    ctx: CanvasRenderingContext2D,
    color: string,
    radius: number,
    x: number,
    y: number,
    alpha = 1,
  ): void {
    const sprite = this.get(color, radius);
    if (!sprite) return;
    const size = sprite.width * (radius / this.quantize(radius));
    const halfSize = size / 2;
    if (alpha < 1) ctx.globalAlpha = alpha;
    ctx.drawImage(sprite, x - halfSize, y - halfSize, size, size);
    if (alpha < 1) ctx.globalAlpha = 1;
  }

  clear(): void {
    this.cache.clear();
    // A clear is also the one honest moment to retry: it is called on teardown
    // and by tests, never in the render loop.
    this.contextUnavailable = false;
  }
}

/** Shared global instance for all renderers */
export const glowCache = new GlowSpriteCache();
